// src/app/services/fga.service.ts
import { Injectable, Inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag'; // NOSONAR
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { FGAData, GetFGADataResponse } from './models';
import { FGA_API_ENDPOINT } from '../app.module';
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { TaskManagerStateService } from '../service/taskmanager-state.service';

const GET_FGA_DATA = gql`
query getFGADataByGUID($externalUserId: String!) {
  getFGADataByGUID(externalUserId: $externalUserId) {
    user {
      userId
      userPrincipalName
      externalUserId
      email
      firstName
      middleName
      lastName
      displayName
      workdayId
      userActiveFlag
      commonUserAttributes {
        type
        title
        companyName
        alternateCompanyName
        companyCode
        plantName
        department
        supplierNumber
      }
      applicationUserAttribute {
        applicationId
        externalApplicationId
        userAttributes {
          key
          value
        }
      }
      recordCreatedDate
      recordCreatedBy
      recordLastUpdatedDate
      recordLastUpdatedBy
    }
    roles {
      roleId
      applicationId
      applicationCode
      automaticRoleAssignment
      displayName
      roleType
      externalApplicationId
      externalRoleId
      permissions {
        featureName
        moduleName
        action
        condition
      }
      recordCreatedDate
      recordCreatedBy
      recordLastUpdatedDate
      recordLastUpdatedBy
    }
  }
}
`;

@Injectable({ providedIn: 'root' })
export class FgaService {
  private details$ = new BehaviorSubject<FGAData>(null); // NOSONAR
  private readonly apollo: ApolloClient<NormalizedCacheObject>;
  constructor(@Inject(FGA_API_ENDPOINT) private readonly endpoint: string, private readonly router: Router, private readonly state: TaskManagerStateService) {
    // Create the HTTP Link
    const httpLink = createHttpLink({ uri: endpoint });

    // Create the Authorization Link
    const authLink = setContext((_, { headers }) => {
      const token = sessionStorage.getItem('accessToken'); // Retrieve the access token
      return {
        headers: {
          ...headers,
          Authorization: token ? `Bearer ${token}` : '',
        },
      };
    });

    // Combine Links
    const link = ApolloLink.from([authLink, httpLink]) as any;

    // Initialize Apollo Client
    this.apollo = new ApolloClient({
      link,
      cache: new InMemoryCache(),
    });
  }

  // async fetchFgaDetails(upn: string): Promise<void> {
  //     try {
  //         const result: any = await this.apollo.query<GetFGADataResponse>({
  //             query: GET_FGA_DATA,
  //             variables: { userPrincipalName: upn },
  //             fetchPolicy: 'no-cache'
  //         });
  //         const data = result?.data.getFGAData;
  //         sessionStorage.setItem('FGA', JSON.stringify(data));
  //         this.details$.next(data);
  //     } catch {
  //         // this.router.navigate(['/unauthorized']);
  //     }
  // }

  async fetchFgaDetails(externalUserId: string): Promise<boolean> {
    try {
      const result = await this.apollo.query<GetFGADataResponse>({
        query: GET_FGA_DATA,
        variables: { externalUserId },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      });
  
      const data = result?.data?.getFGADataByGUID;
      console.info('FGA DATA:', data);
  
      if (!data) {
        return false;
      }
  
      sessionStorage.setItem('FGA', JSON.stringify(data));
      this.details$.next(data);
      this.state.details$.next(data);
      return this.hasTaskManagerAccess(); // ✅ Return access result
    } catch (err) {
      console.info('GraphQL query error:', err);
      return false; // ✅ Handle failure gracefully
    }
  }
  

  loadFromCache(): void {
    const raw = sessionStorage?.getItem('FGA');
    if (raw){
      this.details$.next(JSON.parse(raw));
      this.state.details$.next(JSON.parse(raw));
    } 
  }

  getDetails(): Observable<FGAData> {
    return this.details$.asObservable();
  }

  hasTaskManagerAccess(): boolean {
    const roles = this.details$.value?.roles;
    console.info('[FGA] Roles:', roles);
  
    return roles?.some(role => (role.displayName == 'Task Manager User' || role.displayName == 'Task Manager Admin') && role.permissions?.some(p => p.featureName == 'Task Manager') ) ?? false; // NOSONAR
  }
  
  loadFgaDetails() {
    const details = JSON.parse(sessionStorage.getItem('FGA')) as FGAData;
    this.details$.next(details);
}

  getFgaDetails(): Observable<FGAData> {
    return this.details$.asObservable();
  }

  hasAccess(featureId: string, action?: string): boolean {
    const fgaDetails = this.details$.value;
    // Early return for invalid fgaDetails or roles
    if (!fgaDetails?.roles?.length) {
      return false;
    }
    // Helper function to check if the permission contains the required action
    const hasPermissionForAction = (permission: any, action: string) => {
      return (
        permission.action.includes('All') || permission.action.includes(action)
      );
    };
    // Loop through roles and find the permission for the feature
    for (const role of fgaDetails.roles) {
      const permission = role.permissions?.find((perm) => perm.featureName == featureId); // NOSONAR
      if (!permission) {
        continue;
      }
      if (permission) {
        // If action is specified, check if it's valid
        if (action) {
          if (hasPermissionForAction(permission, action)) {
            return true;
          }
        } else {
          // If no action is specified, return true if permission exists
          return true;
        }
      }
    }
    // Return false if no matching permission is found
    return false;
  }
  hasModule(moduleName: string | string[]): boolean { // NOSONAR
    let fgaDetails = this.details$.value;
    // If not yet loaded, attempt to load synchronously
    if (!fgaDetails) {
      const fromSession = sessionStorage.getItem('FGA');
      if (fromSession) {
        this.details$.next(JSON.parse(fromSession));
        fgaDetails = JSON.parse(fromSession);
      } else {
        console.info('No FGA data available');
        return false;
      }
    }
    if (!fgaDetails || !fgaDetails?.roles) {
      console.info('Here');
      return false;
    }
    console.info("Checking", moduleName);
    for (const role of fgaDetails.roles) {
      if (!role.permissions) {
        continue;
      }
      if (typeof moduleName == 'string') { // NOSONAR
        const permission = role.permissions.find((perm) => perm.moduleName == moduleName); // NOSONAR
        console.info("Checking", moduleName, permission);
        if (!permission) {
          continue;
        }
      } else {
        const permission = role.permissions.find((perm) =>
          moduleName.includes(perm.moduleName)
        );
        console.info("Checking", moduleName, permission);
        if (!permission) {
          continue;
        }
      }
      return true;
    }
    return false;
  }
}